import PropTypes from "prop-types";
import { debounce } from "lodash";
import { t } from "@/i18n-js/instance";
import { formattedDateTime } from "@/react/helpers/dateTimeHelpers/formattedDateTime";
import { dateStringToTimeAgo } from "@/react/helpers/dateTimeHelpers/timeAgo";
import { useStudents } from "@circle-react/components/Spaces/CourseSpace/AdminSummary/Engagement/hooks/useStudents";
import { Icon } from "@circle-react-shared/Icon";
import { DataTable } from "@circle-react-uikit/DataTableV2";
import { Typography } from "@circle-react-uikit/Typography";
import { ProfileButton } from "./ProfileButton";
import { ProgressButton } from "./ProgressButton";

const columns = space => [
  {
    header: t("courses.admin_summary.engagement.members.columns.name"),
    accessorKey: "name",
    thClassName: "border-none",
    cellClassName: "w-auto max-w-[14rem] md:max-w-sm border-none !py-3 w-auto",
    cell: ({ row }) => <ProfileButton student={row.original} />,
  },
  {
    header: t("courses.admin_summary.engagement.members.columns.progress"),
    accessorKey: "progress",
    thClassName: "hidden sm:table-cell border-none",
    cellClassName: "hidden sm:table-cell min-w-[8rem] border-none !py-3",
    cell: ({ row }) => (
      <ProgressButton space={space} student={row.original} variant="full" />
    ),
  },
  {
    header: t("courses.admin_summary.engagement.members.columns.progress"),
    accessorKey: "progress",
    thClassName: "table-cell sm:hidden border-none",
    cellClassName: "sm:hidden table-cell border-none !py-3",
    cell: ({ row }) => (
      <ProgressButton space={space} student={row.original} variant="small" />
    ),
  },
  {
    header: t("courses.admin_summary.engagement.members.columns.last_active"),
    accessorKey: "last_active",
    thClassName: "border-none hidden sm:table-cell",
    cellClassName: "border-none hidden sm:table-cell !py-3",
    cell: ({ row }) => {
      const { last_active } = row.original;
      return last_active ? dateStringToTimeAgo(last_active) : "";
    },
  },
  {
    header: t("courses.admin_summary.engagement.members.columns.start_date"),
    accessorKey: "start_date",
    thClassName: "border-none hidden sm:table-cell",
    cellClassName: "border-none hidden sm:table-cell !py-3",
    cell: ({ row }) => {
      const { start_date } = row.original;
      return start_date
        ? formattedDateTime({
            dateTime: new Date(start_date),
            format: "short_month_and_date_with_year",
          })
        : "";
    },
  },
];

export const StudentsTable = ({ space }) => {
  const {
    records: students,
    isLoading,
    perPage,
    page,
    totalCount,
    loadPage,
    searchQuery,
    setSearchQuery,
  } = useStudents({ space, searchQuery });

  const debouncedSetSearchQuery = debounce(setSearchQuery, 300);

  if (isLoading) {
    return null;
  }

  if (students.length === 0 && !searchQuery.length) {
    return (
      <div className="mx-auto h-full max-w-lg py-10">
        <div className="text-dark mb-1 text-center md:mb-4">
          <Typography.TitleMd as="h4" weight="semibold">
            {t("courses.admin_summary.engagement.members.empty.title")}
          </Typography.TitleMd>
          <div>
            <Typography.BodySm weight="medium">
              {t("courses.admin_summary.engagement.members.empty.description")}
            </Typography.BodySm>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="relative mb-1 mt-5">
        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
          <Icon type="search-v2" size={20} className="text-default" />
        </div>
        <input
          type="search"
          className="text-dark border-primary bg-primary placeholder:text-light block w-full rounded-lg border p-2 pl-10 text-sm placeholder:opacity-100 focus:ring-0 focus:ring-offset-0"
          placeholder={t(
            "courses.admin_summary.engagement.members.search.placeholder",
          )}
          onChange={e => debouncedSetSearchQuery(e.target.value)}
        />
      </div>
      {students.length === 0 ? (
        <div className="mx-auto h-full max-w-lg py-10">
          <div className="text-dark text-center">
            <Typography.TitleMd as="h4" weight="medium">
              {t("courses.admin_summary.engagement.members.search.empty.title")}
            </Typography.TitleMd>
            <div className="mt-5">
              <Typography.BodySm weight="medium">
                {t(
                  "courses.admin_summary.engagement.members.search.empty.description",
                )}
              </Typography.BodySm>
            </div>
          </div>
        </div>
      ) : (
        <DataTable
          columns={columns(space)}
          data={students}
          isLoading={isLoading}
          totalRecordsCount={totalCount}
          pageSize={perPage}
          initialPage={page}
          onChangePage={loadPage}
        >
          <DataTable.Table>
            <DataTable.Header className="!border-none" />
            <DataTable.Loader />
            <DataTable.Body />
          </DataTable.Table>
          {totalCount > perPage && (
            <>
              <hr className="border-primary my-1.5 h-[1px] overflow-hidden border-t" />
              <DataTable.Pagination align="left" direction="reverse" />
            </>
          )}
        </DataTable>
      )}
    </>
  );
};

StudentsTable.propTypes = {
  space: PropTypes.object.isRequired,
};
